import React, {useEffect, useState} from 'react';
import ViewerContainer from '../PdfViewer/ViewerContainer/ViewerContainer';
import {useTranslation} from "react-i18next";
import {Box} from '@mui/material';
import {API_BASE} from '../../apiBase';
import {apiFetch} from '../../api/api';

export default function ToolConfigStep({
                                           selectedAnnotations,
                                           allAnnotations,
                                           setAllAnnotations,
                                           setSelectedAnnotations,
                                           groupMeta,
                                           setGroupMeta,
                                           onExampleFileSelected,
                                           exampleFile,
                                           useOcr,
                                           setUseOcr,
                                           useDeepScan,
                                           setUseDeepScan,
                                           allowReExtraction,
                                           setAllowReExtraction,
                                           spatialCloseness,
                                           setSpatialCloseness,
                                           useSpatialLocality,
                                           setUseSpatialLocality,
                                           toolDetails,
                                           setToolDetails
                                       }) {
    const {t} = useTranslation();

    const [configuredProviders, setConfiguredProviders] = useState([]);
    const [availableModels, setAvailableModels] = useState({});

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const res = await apiFetch(`${API_BASE}/profile`);
                if (!res.ok) throw new Error('Failed to fetch profile');
                const profile = await res.json();

                const configured = Object.keys(profile.configured_api_keys || {});
                setConfiguredProviders(configured);
                setAvailableModels(profile.allowed || {});

                // Only set default provider and model if they haven't been set yet
                if (!toolDetails.provider && !toolDetails.model) {
                    // Priority 1: Use last used provider/model (shared context)
                    const lastProvider = profile.last_used_provider;
                    const lastModel = profile.last_used_model;

                    if (lastProvider && lastModel &&
                        configured.includes(lastProvider) &&
                        profile.allowed?.[lastProvider]?.includes(lastModel)) {
                        setToolDetails(prev => ({
                            ...prev,
                            provider: lastProvider,
                            model: lastModel
                        }));
                    }
                    // Priority 2: Use default provider and model from admin settings
                    else {
                        const defaultProviderFromProfile = profile.default_provider;
                        const defaultModelFromProfile = profile.default_model;

                        if (defaultProviderFromProfile && configured.includes(defaultProviderFromProfile)) {
                            if (defaultModelFromProfile) {
                                setToolDetails(prev => ({
                                    ...prev,
                                    provider: defaultProviderFromProfile,
                                    model: defaultModelFromProfile
                                }));
                            } else if (profile.allowed?.[defaultProviderFromProfile]?.[0]) {
                                setToolDetails(prev => ({
                                    ...prev,
                                    provider: defaultProviderFromProfile,
                                    model: profile.allowed[defaultProviderFromProfile][0]
                                }));
                            }
                        } else if (configured.length > 0) {
                            // Fallback to first available provider
                            const firstProvider = configured[0];
                            setToolDetails(prev => ({
                                ...prev,
                                provider: firstProvider,
                                model: profile.allowed?.[firstProvider]?.[0] || ''
                            }));
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to fetch user profile:', err);
            }
        };

        fetchUserProfile();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Don't prevent auto-extract in tool creation - always allow re-extraction when OCR mode changes
    console.log('[ToolConfigStep] allAnnotations.length=', allAnnotations?.length, 'useOcr=', useOcr);

    const handleProviderChange = async (newProvider) => {
        // Clear the model when provider changes - user must explicitly select a model
        setToolDetails(prev => ({
            ...prev,
            provider: newProvider,
            model: ''
        }));
    };

    const handleModelChange = async (newModel) => {
        setToolDetails(prev => ({
            ...prev,
            model: newModel
        }));

        // Save the last used provider/model (shared context) only if both are selected
        if (toolDetails.provider && newModel) {
            try {
                await apiFetch(`${API_BASE}/user/preferences/last-used`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        provider: toolDetails.provider,
                        model: newModel
                    })
                });
            } catch (err) {
                console.error('Failed to save last used provider/model:', err);
            }
        }
    };

    return (
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0}}>
            <ViewerContainer
                showHeader
                compactHeader
                disableExtractionMode="off"
                preventAutoExtract={false}
                selectedAnnotations={selectedAnnotations}
                setSelectedAnnotations={setSelectedAnnotations}
                allAnnotations={allAnnotations}
                onAllAnnotationsChange={setAllAnnotations}
                groupMeta={groupMeta}
                setGroupMeta={setGroupMeta}
                onFileSelected={onExampleFileSelected}
                file_input={exampleFile}
                tableWarningText={t("table_warning")}
                persistSelection={true}
                useOcrExternal={useOcr}
                setUseOcrExternal={(newUseOcr) => {
                    console.log('[ToolConfigStep] OCR mode changing to:', newUseOcr);
                    // When OCR mode changes, temporarily allow re-extraction
                    setAllowReExtraction(true);
                    setUseOcr(newUseOcr);
                }}
                useDeepScanExternal={useDeepScan}
                setUseDeepScanExternal={setUseDeepScan}
                spatialCloseness={spatialCloseness}
                setSpatialCloseness={setSpatialCloseness}
                useSpatialLocality={useSpatialLocality}
                setUseSpatialLocality={setUseSpatialLocality}
                providerExternal={toolDetails.provider}
                setProviderExternal={handleProviderChange}
                modelExternal={toolDetails.model}
                setModelExternal={handleModelChange}
                availableModelsExternal={availableModels}
                configuredProvidersExternal={configuredProviders}
            />
        </Box>
    );
}